'use client';

import React, { useRef, useEffect } from 'react';
import { Section } from '@/components/ui/Section';
import styles from './KioskFeatures.module.css';

export interface KioskFeature {
  number: string;
  title: string;
  description: string;
  badge: string;
}

export interface KioskFeaturesProps {
  title: string;
  subtitle?: string;
  features: KioskFeature[];
}

/**
 * KioskFeatures Component
 * Displays numbered features in an alternating layout
 */
export function KioskFeatures({ title, subtitle, features }: KioskFeaturesProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = sectionRef.current?.querySelectorAll(`.${styles.feature}`);
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.section} ref={sectionRef}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {subtitle && (
          <p className={styles.sectionSubtitle}>{subtitle}</p>
        )}

        <div className={styles.features}>
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${styles.feature} ${index % 2 === 1 ? styles.featureReverse : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={styles.featureNumber}>
                <span className={styles.numberText}>/ {feature.number}</span>
                <div className={styles.numberLine} />
              </div>

              <div className={styles.featureContent}>
                <div className={styles.badge}>{feature.badge}</div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </Section>
  );
}

